"use client";

// The TipTap chat input: paragraphs + hard breaks + skill ("/") and artifact
// ("@") mention chips, Enter-submits, large-paste-to-file conversion.
// Ported from the private `@magnus/react` editor (tiptap-skill-input.tsx);
// host integrations arrive via `skillMentionOptions` / `artifactMentionOptions`
// / `renderSkillHover` seams. Single source for npm + registry + shims.
import type { Editor } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import History from "@tiptap/extension-history";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import { cn } from "@workspace/ui/lib/utils";
import { type ReactNode, type Ref, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { type ArtifactMentionOptions, useArtifactMention } from "./artifact-mention";
import type { ArtifactPickerItem, SkillPickerItem } from "./refs";
import { type EditorNodeJSON, type EditorPart, partsToPromptString, promptToTiptapDoc, serializeDocToParts } from "./serialize";
import { isSuggestionActive, SkillChipHoverProvider, type SkillMentionAttrs, type SkillMentionOptions, toMentionAttrs, useSkillMention } from "./skill-mention";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TipTapSkillInputHandle = {
  focus: () => void;
  clear: () => void;
  setContent: (text: string) => void;
  /** Populate editor from a canonical prompt string. `{{skill:id|name}}`
   *  tokens render as mention chips; `\n\n` becomes a paragraph break and a
   *  single `\n` becomes a line break (Shift+Enter). */
  setContentFromPrompt: (prompt: string) => void;
  getPlainText: () => string;
  isEmpty: () => boolean;
  getParts: () => EditorPart[];
  insertSkill: (item: SkillPickerItem) => void;
  /** Insert plain text at the cursor, preserving rich content (mention
   *  chips). Adds a separating space when the cursor butts against a word
   *  or a chip. The dictation seam's transcript lands here. */
  insertText: (text: string) => void;
};

/**
 * When pasted plain text exceeds this many characters we convert it into a
 * synthetic `text/plain` file attachment instead of inserting it inline. This
 * keeps the composer readable for huge logs / docs and routes the content
 * through the normal file-upload + processing pipeline.
 */
export const PASTE_AS_FILE_THRESHOLD = 6000;

// Stable identity so omitting `availableArtifacts` doesn't mint a new array
// each render (which would churn the artifact-mention hook's ref).
const EMPTY_ARTIFACTS: ArtifactPickerItem[] = [];
type TipTapSkillInputProps = {
  availableSkills: SkillPickerItem[];
  skillsLoading?: boolean;
  /** Artifacts the viewer can `@`-mention. Only consulted when
   *  {@link TipTapSkillInputProps.enableArtifactMentions} is true. */
  availableArtifacts?: ArtifactPickerItem[];
  artifactsLoading?: boolean;
  /** Register the `@` artifact-mention extension. Off by default so editors
   *  that only reference agents don't pop an artifact picker on `@`. */
  enableArtifactMentions?: boolean;
  /** Host integrations for the "/" picker (category labels, manage link). */
  skillMentionOptions?: SkillMentionOptions;
  /** Host integrations for the "@" picker (manage link). */
  artifactMentionOptions?: ArtifactMentionOptions;
  /** Wraps skill chips with a hover card (e.g. the host's skill detail
   *  popover). Absent → plain chips. */
  renderSkillHover?: (skillId: string, chip: ReactNode) => ReactNode;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** When true (default), pressing Enter submits the nearest form.
   *  Set false for multi-line prompts where Enter should insert a newline. */
  submitOnEnter?: boolean;
  className?: string;
  /** Overrides height/overflow utilities on the contentEditable element
   *  itself. Defaults to the compact chat-composer sizing. */
  editorClassName?: string;
  onPasteFiles?: (files: File[]) => void;
  onContentChange?: (isEmpty: boolean) => void;
  /**
   * Character count above which a plain-text paste is converted into a
   * `pasted-text-*.txt` file attachment. Set to `Infinity` to disable.
   * Defaults to {@link PASTE_AS_FILE_THRESHOLD}.
   */
  pasteAsFileThreshold?: number;
  /** Fires on every content change with the list of skill IDs currently
   *  present as mentions in the document. */
  onSkillsChange?: (ids: string[]) => void;
  /**
   * Controlled draft value (canonical prompt string — `{{skill:id|name}}`
   * tokens + text; plain text works). One-way sync: external changes are
   * applied to the editor; editor changes flow out via `onValueChange`.
   * Omit for an uncontrolled editor.
   */
  value?: string;
  /** Fires on every content change with the canonical prompt string. */
  onValueChange?: (value: string) => void;
  /** Skill to pre-insert as a chiclet on mount. Insertion waits for both the
   *  editor to exist AND the doc to still be empty — we never overwrite what
   *  the user typed. */
  initialSkill?: SkillPickerItem | null;
  /** Canonical prompt string (`{{skill:id|name}}` tokens + text) to seed
   *  the editor with once on mount. Only applied while the editor is
   *  still empty; we never clobber user input. */
  initialPrompt?: string;
};
function collectSkillIds(editor: Editor): string[] {
  const ids: string[] = [];
  editor.state.doc.descendants(node => {
    if (node.type.name === "mention") {
      const attrs = node.attrs as SkillMentionAttrs;
      if (attrs.id) {
        ids.push(attrs.id);
      }
    }
  });
  return ids;
}

// ---------------------------------------------------------------------------
// Document → message parts
//
// The pure data-mapping (doc JSON ⇄ parts ⇄ canonical prompt string) lives in
// `./serialize` so it can be unit-tested without a live editor. The only
// piece that needs the `Editor` instance is `serializeEditorToParts`, which
// is a thin wrapper over `editor.getJSON()`.
// ---------------------------------------------------------------------------

export function serializeEditorToParts(editor: Editor): EditorPart[] {
  return serializeDocToParts(editor.getJSON() as EditorNodeJSON);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function TipTapSkillInput({
  ref,
  availableSkills,
  skillsLoading = false,
  availableArtifacts = EMPTY_ARTIFACTS,
  artifactsLoading = false,
  enableArtifactMentions = false,
  skillMentionOptions,
  artifactMentionOptions,
  renderSkillHover,
  placeholder = "What would you like to know?",
  disabled = false,
  autoFocus = true,
  submitOnEnter = true,
  className,
  editorClassName,
  onPasteFiles,
  onContentChange,
  onSkillsChange,
  onValueChange,
  value,
  initialSkill,
  initialPrompt,
  pasteAsFileThreshold = PASTE_AS_FILE_THRESHOLD
}: TipTapSkillInputProps & {
  ref?: Ref<TipTapSkillInputHandle>;
}) {
  const {
    extension: SkillMention
  } = useSkillMention(availableSkills, skillsLoading, "top-start", skillMentionOptions);
  // The extension is always constructed (rules of hooks) but only added to
  // the schema when the caller opts in — see `enableArtifactMentions`.
  const {
    extension: ArtifactMention
  } = useArtifactMention(availableArtifacts, artifactsLoading, "top-start", artifactMentionOptions);
  const onPasteFilesRef = useRef(onPasteFiles);
  onPasteFilesRef.current = onPasteFiles;
  const pasteAsFileThresholdRef = useRef(pasteAsFileThreshold);
  pasteAsFileThresholdRef.current = pasteAsFileThreshold;
  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;
  const onSkillsChangeRef = useRef(onSkillsChange);
  onSkillsChangeRef.current = onSkillsChange;
  const onValueChangeRef = useRef(onValueChange);
  onValueChangeRef.current = onValueChange;

  // Extensions are installed once. Read current copy without rebuilding the
  // editor (which would discard the draft, selection, and undo history).
  const placeholderRef = useRef(placeholder);
  placeholderRef.current = placeholder;

  // Canonical prompt string for the controlled-value seam (C1): text plus
  // `{{skill:id|name}}` tokens, so drafts round-trip skill chips.
  const canonicalOf = useCallback((e: Editor) => partsToPromptString(serializeEditorToParts(e)), []);
  // Echo guard for the one-way value sync: a `value` change that originated
  // from this editor's own onUpdate must not be written back.
  const lastEmittedValueRef = useRef<string | null>(null);
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [Document, Paragraph, Text, HardBreak, History, Placeholder.configure({
      placeholder: () => placeholderRef.current
    }), SkillMention, ...(enableArtifactMentions ? [ArtifactMention] : [])],
    editable: !disabled,
    autofocus: autoFocus ? "end" : false,
    onCreate: ({
      editor: e_0
    }) => {
      onContentChangeRef.current?.(e_0.isEmpty);
      onSkillsChangeRef.current?.(collectSkillIds(e_0));
    },
    onUpdate: ({
      editor: e_1
    }) => {
      onContentChangeRef.current?.(e_1.isEmpty);
      onSkillsChangeRef.current?.(collectSkillIds(e_1));
      const canonical = canonicalOf(e_1);
      lastEmittedValueRef.current = canonical;
      onValueChangeRef.current?.(canonical);
    },
    editorProps: {
      attributes: {
        class: cn("w-full overflow-y-auto p-3 text-left text-base outline-none md:text-sm", "max-h-48 min-h-16", "[&_p]:m-0",
        // Placeholder: laid over the empty first paragraph on ONE line,
        // ellipsized when the editor is narrower than the copy. The usual
        // `float-left; height: 0` recipe lets a long placeholder wrap onto
        // lines the editor's min-height doesn't cover, so in a narrow
        // column (Create's chat rail) the text was clipped mid-sentence.
        "[&_p.is-editor-empty:first-child]:relative", "[&_p.is-editor-empty:first-child::before]:text-muted-foreground", "[&_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]", "[&_p.is-editor-empty:first-child::before]:absolute", "[&_p.is-editor-empty:first-child::before]:inset-x-0", "[&_p.is-editor-empty:first-child::before]:top-0", "[&_p.is-editor-empty:first-child::before]:truncate", "[&_p.is-editor-empty:first-child::before]:pointer-events-none", editorClassName)
      },
      handleKeyDown: (view, event) => {
        if (submitOnEnter && event.key === "Enter" && !event.shiftKey && !event.isComposing) {
          if (isSuggestionActive(view.state)) {
            return false;
          }
          event.preventDefault();
          const form = view.dom.closest("form");
          if (form) {
            try {
              form.requestSubmit();
            } catch (err) {
              // requestSubmit can throw in some embedded webviews. Fall
              // back to dispatching the submit event directly so the user
              // never silently loses an Enter keypress.
              console.error("[TipTap] requestSubmit() failed:", err);
              form.dispatchEvent(new Event("submit", {
                bubbles: true,
                cancelable: true
              }));
            }
          }
          return true;
        }
        return false;
      },
      handlePaste: (_view, event_0) => {
        const clipboard = event_0.clipboardData;
        if (!clipboard) {
          return false;
        }
        const files: File[] = [];
        for (const item of clipboard.items) {
          if (item.kind === "file") {
            const file = item.getAsFile();
            if (file) {
              files.push(file);
            }
          }
        }
        if (files.length > 0) {
          event_0.preventDefault();
          onPasteFilesRef.current?.(files);
          return true;
        }

        // Convert very large plain-text pastes into a synthetic .txt file so
        // they go through the normal upload pipeline instead of bloating the
        // composer (and the eventual user message). We only do this when no
        // real files were pasted and the handler is wired up.
        const pasteFiles = onPasteFilesRef.current;
        const threshold = pasteAsFileThresholdRef.current;
        if (pasteFiles && Number.isFinite(threshold)) {
          const text = clipboard.getData("text/plain");
          if (text && text.length > threshold) {
            event_0.preventDefault();
            const filename = `pasted-text-${new Date().toISOString().replace(/[:.]/g, "-")}.txt`;
            const file_0 = new File([text], filename, {
              type: "text/plain",
              lastModified: Date.now()
            });
            pasteFiles([file_0]);
            return true;
          }
        }
        return false;
      }
    }
  });
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: copy changes must refresh the extension's ref-backed decorations.
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      // Recompute empty-editor decorations when only the copy changes.
      editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false));
    }
  }, [editor, placeholder]);

  // Insert the pre-populated skill chiclet once both the editor exists
  // and the initialSkill is known. Guarded by a ref so we don't retry
  // after first successful insertion, and skipped if the user already
  // typed something — we never overwrite their content.
  const insertedInitialSkillRef = useRef(false);
  useEffect(() => {
    if (insertedInitialSkillRef.current) {
      return;
    }
    if (!(editor && initialSkill)) {
      return;
    }
    if (!editor.isEmpty) {
      insertedInitialSkillRef.current = true;
      return;
    }
    insertedInitialSkillRef.current = true;
    queueMicrotask(() => {
      if (editor.isDestroyed || !editor.isEmpty) {
        return;
      }
      editor.chain().insertContent({
        type: "mention",
        attrs: toMentionAttrs(initialSkill)
      }).insertContent(" ").focus("end").run();
    });
  }, [editor, initialSkill]);

  // Seed canonical prompt (`{{skill:id|name}}` tokens + text) on mount.
  // Same guard as initialSkill: one-shot, never overwrites user input.
  // setContent calls flushSync internally; defer to a microtask so it
  // doesn't collide with React's render phase.
  const seededInitialPromptRef = useRef(false);
  useEffect(() => {
    if (seededInitialPromptRef.current) {
      return;
    }
    if (!(editor && initialPrompt)) {
      return;
    }
    if (!editor.isEmpty) {
      seededInitialPromptRef.current = true;
      return;
    }
    seededInitialPromptRef.current = true;
    queueMicrotask(() => {
      if (editor.isDestroyed) {
        return;
      }
      editor.commands.setContent(promptToTiptapDoc(initialPrompt));
    });
  }, [editor, initialPrompt]);

  // One-way controlled-value sync (C1): apply external `value` changes to
  // the editor. Skips echoes of this editor's own emissions and no-op
  // writes, so typing never re-renders through the parent. The imperative
  // `setContent` handle remains the escape hatch (dictation, chips).
  useEffect(() => {
    if (!(editor && value !== undefined)) {
      return;
    }
    if (value === lastEmittedValueRef.current) {
      return;
    }
    if (value === canonicalOf(editor)) {
      lastEmittedValueRef.current = value;
      return;
    }
    lastEmittedValueRef.current = value;
    queueMicrotask(() => {
      if (editor.isDestroyed) {
        return;
      }
      editor.commands.setContent(value ? promptToTiptapDoc(value) : "");
    });
  }, [editor, value, canonicalOf]);
  useImperativeHandle(ref, () => ({
    focus: () => editor?.commands.focus(),
    clear: () => editor?.commands.clearContent(),
    setContent: (text_0: string) => {
      if (!editor) {
        return;
      }
      editor.commands.setContent(text_0 ? `<p>${escapeHtml(text_0)}</p>` : "");
      editor.commands.focus("end");
    },
    setContentFromPrompt: (prompt: string) => {
      if (!editor) {
        return;
      }
      // Defer through a microtask: setContent calls flushSync, which
      // can't run synchronously inside a React lifecycle.
      queueMicrotask(() => {
        if (editor.isDestroyed) {
          return;
        }
        if (!prompt) {
          editor.commands.setContent("");
          return;
        }
        editor.commands.setContent(promptToTiptapDoc(prompt));
        editor.commands.focus("end");
      });
    },
    getPlainText: () => editor?.getText() ?? "",
    isEmpty: () => editor?.isEmpty ?? true,
    getParts: () => editor ? serializeEditorToParts(editor) : [],
    insertSkill: (item_0: SkillPickerItem) => {
      if (!editor) {
        return;
      }
      editor.chain().focus().insertContent({
        type: "mention",
        attrs: toMentionAttrs(item_0)
      }).insertContent(" ").run();
    },
    insertText: (text_1: string) => {
      if (!(editor && text_1)) {
        return;
      }
      // A text-node insert (not an HTML string) so dictated `<`/`&`
      // can't parse as markup. Leading space when the cursor butts
      // against a word or a chip — never at a blank doc start.
      const {
        from
      } = editor.state.selection;
      const nodeBefore = editor.state.doc.resolve(from).nodeBefore;
      const needsSpace = nodeBefore ? nodeBefore.isText ? !/\s$/.test(nodeBefore.text ?? "") : true : false;
      editor.chain().focus().insertContent({
        type: "text",
        text: `${needsSpace ? " " : ""}${text_1}`
      }).run();
    }
  }), [editor]);
  return <SkillChipHoverProvider render={renderSkillHover ?? null}>
      <div className={cn("relative min-h-16 w-full flex-1", className)} data-tiptap-root="">
        {!editor &&
      // Same sizing overrides as the live editor, or the pre-hydration
      // placeholder is a different height and the frame jumps.
      <div className={cn("max-h-48 min-h-16 w-full p-3 text-left text-base text-muted-foreground md:text-sm", editorClassName)}>
            {placeholder}
          </div>}
        <EditorContent className="w-full" editor={editor} />
      </div>
    </SkillChipHoverProvider>;
}
function escapeHtml(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}